import React from "react"
import { Box, Container, Grid, List, ListItem, ListItemText } from "@mui/material"

import SEO from "@secureo/common/components/SEO"
import BreadCrumbs, { BreadCrumb } from "components/BreadCrumbs"

import { helpCenterAncestorBreadCrumbs } from "./index"
import { fallBackPhoneNumber } from "config/contact"

import { CustomAppProps } from "pages/_app"

const overviewLinks = [
	{
		title: "Widerrufsrecht",
		anchorLink: "#widerrufsrecht",
	},
	{
		title: "Folgen des Widerrufs",
		anchorLink: "#folgen-des-widerrufs",
	},
	{
		title: "Ausschluss- bzw. Erlöschensgründe",
		anchorLink: "#ausschluss-erloeschungsgruende",
	},
	{
		title: "Muster-Widerrufsformular",
		anchorLink: "#muster-widerrufsformular",
	},
]

const CancellationPolicyPage = ({ asPath, locale }: CustomAppProps) => {
	const currentPath: BreadCrumb = {
		href: "/help/cancellation-policy",
		label: "Widerrufsbelehrung",
	}

	return (
		<>
			<SEO asPath={asPath} appendLocalizedShopNameWithPipe title="Widerrufsbelehrung" />
			<div style={{ position: "relative", overflow: "hidden" }}>
				<img
					alt="background"
					src="https://res.cloudinary.com/digitalzylinder-shop/image/upload/v1590648703/DigiShop2/Content/Imprint-Bg-Circle.png"
					style={{
						position: "absolute",
						width: "100%",
						height: 500,
						top: 0,
						left: 0,
						zIndex: -1,
					}}
				/>
				<BreadCrumbs
					locale={locale}
					currentPath={currentPath}
					ancestors={helpCenterAncestorBreadCrumbs}
				/>
				<Box my={5}>
					<Container>
						<Grid container spacing={3}>
							<Grid item xs={12}>
								<div>
									<div
										style={{
											color: "#4691CE",
											textTransform: "uppercase",
											fontSize: 13,
										}}
									>
										<strong>Widerrufsrecht für Verbraucher</strong>
									</div>
									<Box mb={2}>
										<h1 style={{ margin: 0, fontSize: 30 }}>
											Widerrufsbelehrung
										</h1>
									</Box>
									<p style={{ maxWidth: 900 }}>
										(Verbraucher ist jede natürliche Person, die ein
										Rechtsgeschäft zu Zwecken abschließt, die überwiegend weder
										ihrer gewerblichen noch ihrer selbstständigen beruflichen
										Tätigkeit zugerechnet werden kann.)
									</p>
								</div>
							</Grid>
							<Grid item xs={12} md={3}>
								<Box mt={3}>
									<List component="nav" aria-label="overview-imprint">
										<ListItem>
											<div className="h2" style={{ fontSize: 26 }}>
												Übersicht
											</div>
										</ListItem>
										{overviewLinks.map((overviewLink) => {
											const { title, anchorLink } = overviewLink
											return (
												<a href={anchorLink} key={title}>
													<ListItem
														button
														style={{ padding: 0, paddingLeft: 15 }}
													>
														<ListItemText
															primary={title}
															style={{ color: "#505e5f" }}
														/>
													</ListItem>
												</a>
											)
										})}
									</List>
								</Box>
							</Grid>
							<Grid item xs={12} md={9}>
								<Box mt={5} id="widerrufsrecht">
									<h2>Widerrufsrecht</h2>
								</Box>
								<p>
									Sie haben das Recht, binnen vierzehn Tagen ohne Angabe von
									Gründen diesen Vertrag zu widerrufen. Die Widerrufsfrist beträgt
									vierzehn Tage ab dem Tag:
								</p>
								<Box my={3}>
									<p>
										- an dem Sie oder ein von Ihnen benannter Dritter, der nicht
										der Beförderer ist, die Waren in Besitz genommen haben bzw.
										hat, sofern Sie eine oder mehrere Waren im Rahmen einer
										einheitlichen Bestellung bestellt haben und diese
										einheitlich geliefert wird bzw. werden;
									</p>
								</Box>
								<p>
									- an dem Sie oder ein von Ihnen benannter Dritter, der nicht der
									Beförderer ist, die letzte Ware in Besitz genommen haben bzw.
									hat, sofern Sie mehrere Waren im Rahmen einer einheitlichen
									Bestellung bestellt haben und diese getrennt geliefert werden;
								</p>
								<Box my={3}>
									<p>
										- an dem Sie oder ein von Ihnen benannter Dritter, der nicht
										der Beförderer ist, die letzte Teilsendung oder das letzte
										Stück in Besitz genommen haben bzw. hat, sofern Sie eine
										Ware bestellt haben, die in mehreren Teilsendungen oder
										Stücken geliefert wird;
									</p>
								</Box>
								<p>
									Um Ihr Widerrufsrecht auszuüben, müssen Sie uns{" "}
									<strong>
										(secureo GmbH, Im Moos 19, 6067 Absam, Telefonnummer:{" "}
										{fallBackPhoneNumber}, E-Mail-Adresse: office@secureo.at)
									</strong>{" "}
									mittels einer eindeutigen Erklärung (z.B. ein mit der Post
									versandter Brief oder E-Mail) über Ihren Entschluss, diesen
									Vertrag zu widerrufen, informieren. Sie können dafür das
									beigefügte Muster-Widerrufsformular verwenden, das jedoch nicht
									vorgeschrieben ist.
								</p>
								<Box my={3}>
									<p>
										Zur Wahrung der Widerrufsfrist reicht es aus, dass Sie die
										Mitteilung über die Ausübung des Widerrufsrechts vor Ablauf
										der Widerrufsfrist absenden.
									</p>
								</Box>
								<h2 id="folgen-des-widerrufs">Folgen des Widerrufs</h2>
								<Box my={3}>
									<p>
										Wenn Sie diesen Vertrag widerrufen, haben wir Ihnen alle
										Zahlungen, die wir von Ihnen erhalten haben, einschließlich
										der Lieferkosten (mit Ausnahme der zusätzlichen Kosten, die
										sich daraus ergeben, dass Sie eine andere Art der Lieferung
										als die von uns angebotene, günstigste Standardlieferung
										gewählt haben), unverzüglich und spätestens binnen vierzehn
										Tagen ab dem Tag zurückzuzahlen, an dem die Mitteilung über
										Ihren Widerruf dieses Vertrags bei uns eingegangen ist. Für
										diese Rückzahlung verwenden wir dasselbe Zahlungsmittel, das
										Sie bei der ursprünglichen Transaktion eingesetzt haben, es
										sei denn, mit Ihnen wurde ausdrücklich etwas anderes
										vereinbart; in keinem Fall werden Ihnen wegen dieser
										Rückzahlung Entgelte berechnet.
									</p>
								</Box>
								<p>
									Wir können die Rückzahlung verweigern, bis wir die Waren wieder
									zurückerhalten haben oder bis Sie den Nachweis erbracht haben,
									dass Sie die Waren zurückgesandt haben, je nachdem, welches der
									frühere Zeitpunkt ist.
								</p>
								<Box my={3}>
									<p>
										Sie haben die Waren unverzüglich und in jedem Fall
										spätestens binnen vierzehn Tagen ab dem Tag, an dem Sie uns
										über den Widerruf dieses Vertrags unterrichten, an uns
										zurückzusenden oder zu übergeben. Die Frist ist gewahrt,
										wenn Sie die Waren vor Ablauf der Frist von vierzehn Tagen
										absenden.
									</p>
								</Box>
								<p>
									Sie tragen die unmittelbaren Kosten der Rücksendung der Waren.
								</p>
								<Box my={3}>
									<p>
										Sie müssen für einen etwaigen Wertverlust der Waren nur
										aufkommen, wenn dieser Wertverlust auf einen zur Prüfung der
										Beschaffenheit, Eigenschaften und Funktionsweise der Waren
										nicht notwendigen Umgang mit ihnen zurückzuführen ist.
									</p>
								</Box>
								<Box mb={3} id="ausschluss-erloeschungsgruende">
									<h2>Ausschluss- bzw. Erlöschensgründe</h2>
								</Box>
								<p>Das Widerrufsrecht besteht nicht bei Verträgen</p>
								<Box mt={3}>
									- zur Lieferung von Waren, die nicht vorgefertigt sind und für
									deren Herstellung eine individuelle Auswahl oder Bestimmung
									durch den Verbraucher maßgeblich ist oder die eindeutig auf die
									persönlichen Bedürfnisse des Verbrauchers zugeschnitten sind.
									Insbesondere gilt dies für Schließzylinder, Schließanlagen,
									Profilzylinder in Gleichschließung, Nachlieferungen von
									Nachschlüsseln und Profilzylindern auf bestehende Schließungen,
									die auf Kundenwunsch angefertigt wurden. Der Kunde tätigt eine
									individuelle Auswahl, sobald er von den Voreinstellungen auf der
									jeweiligen Produktseite abweicht.
								</Box>
								<div>
									- zur Lieferung von Waren, die schnell verderben können oder
									deren Verfallsdatum schnell überschritten würde;
								</div>
								<div>
									- zur Lieferung alkoholischer Getränke, deren Preis bei
									Vertragsschluss vereinbart wurde, die aber frühestens 30 Tage
									nach Vertragsschluss geliefert werden können und deren aktueller
									Wert von Schwankungen auf dem Markt abhängt, auf die der
									Unternehmer keinen Einfluss hat;
								</div>
								<div>
									- zur Lieferung von Zeitungen, Zeitschriften oder Illustrierten
									mit Ausnahme von Abonnement-Verträgen.
								</div>
								<Box my={3}>
									<p>Das Widerrufsrecht erlischt vorzeitig bei Verträgen</p>
								</Box>
								<div>
									- zur Lieferung versiegelter Waren, die aus Gründen des
									Gesundheitsschutzes oder der Hygiene nicht zur Rückgabe geeignet
									sind, wenn ihre Versiegelung nach der Lieferung entfernt wurde;
								</div>
								<div>
									- zur Lieferung von Waren, wenn diese nach der Lieferung
									aufgrund ihrer Beschaffenheit untrennbar mit anderen Gütern
									vermischt wurden. Dies umfasst ebenfalls die unwiderrufliche
									Vermischung von Gütern, die durch die Inbetriebnahme und/oder
									Programmierung entstehen kann.
								</div>
								<div>
									- zur Lieferung von Waren, wenn diese nach der Lieferung
									aufgrund ihrer Beschaffenheit untrennbar mit anderen Gütern
									vermischt wurden;
								</div>
								<div>
									- zur Lieferung von Ton- oder Videoaufnahmen oder
									Computersoftware in einer versiegelten Packung, wenn die
									Versiegelung nach der Lieferung entfernt wurde.
								</div>
								<Box my={3}>
									<p>Allgemeine Hinweise zum Widerruf</p>
								</Box>
								<p>
									1) Bitte vermeiden Sie Beschädigungen und Verunreinigungen der
									Ware. Senden Sie die Ware bitte in Originalverpackung mit
									sämtlichem Zubehör und mit allen Verpackungsbestandteilen an uns
									zurück. Verwenden Sie bitte eine schützende Umverpackung. Wenn
									Sie die Originalverpackung nicht mehr besitzen, sorgen Sie bitte
									mit einer geeigneten Verpackung für einen ausreichenden Schutz
									vor Transportschäden.1) Bitte vermeiden Sie Beschädigungen und
									Verunreinigungen der Ware. Senden Sie die Ware bitte in
									Originalverpackung mit sämtlichem Zubehör und mit allen
									Verpackungsbestandteilen an uns zurück. Verwenden Sie bitte eine
									schützende Umverpackung. Wenn Sie die Originalverpackung nicht
									mehr besitzen, sorgen Sie bitte mit einer geeigneten Verpackung
									für einen ausreichenden Schutz vor Transportschäden.
								</p>
								<p>2) Senden Sie die Ware bitte nicht unfrei an uns zurück.</p>
								<p>
									3) Bitte beachten Sie, dass die vorgenannten Ziffern 1-2 nicht
									Voraussetzung für die wirksame Ausübung des Widerrufsrechts
									sind.
								</p>
								<Box
									py={5}
									mt={3}
									style={{ borderTop: "1px dotted grey" }}
									id="muster-widerrufsformular"
								>
									<h2>Muster-Widerrufsformular</h2>
								</Box>
								<p>
									(Wenn Sie den Vertrag widerrufen wollen, dann füllen Sie bitte
									dieses Formular aus und senden Sie es zurück.)
								</p>
								<Box my={3}>
									<p>
										- An secureo GmbH, Im Moos 19, 6067 Absam, E-Mail-Adresse:
										office@secureo.at :
									</p>
								</Box>
								<p>
									- Hiermit widerrufe(n) ich/ wir (*) den von mir/ uns (*)
									abgeschlossenen Vertrag über den Kauf der folgenden Waren (*)/
									die Erbringung der folgenden Dienstleistung (*)
								</p>
								<Box my={3}>
									<p>- Bestellt am (*)/ erhalten am (*)</p>
								</Box>
								<p>- Name des/ der Verbraucher(s)</p>
								<p>- Anschrift des/ der Verbraucher(s)</p>
								<p>
									- Unterschrift des/ der Verbraucher(s) (nur bei Mitteilung auf
									Papier)
								</p>
								<p>- Datum</p>
								<Box mt={3}>
									<p>(*) Unzutreffendes streichen.</p>
								</Box>
							</Grid>
						</Grid>
					</Container>
				</Box>
			</div>
		</>
	)
}

export default CancellationPolicyPage
